import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Location data returned by the sponsor contacts endpoint
 */
export interface SponsorLocation {
  id: string;
  sponsorId: string;
  name: string;
  street1: string;
  street2: string | null;
  city: string;
  state: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  email: string | null;
}

/**
 * Representative data returned by the sponsor contacts endpoint
 */
export interface SponsorRepresentative {
  id: string;
  sponsorId: string;
  name: string;
  title: string | null;
  territory: string | null;
  phone: string | null;
  email: string | null;
}

/**
 * Complete sponsor contact data returned by GET /sponsors/:id/contacts
 */
export type SponsorContactType = 'locations_only' | 'reps_only' | 'mixed' | 'contact_form_only';

export interface SponsorContactData {
  contactType: SponsorContactType;
  contactMessage: string | null;
  locations: SponsorLocation[];
  representatives: SponsorRepresentative[];
  sponsorName: string;
  sponsorLogoUrl: string | null;
  sponsorBrandColor: string | null;
  sponsorWebsite: string | null;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Cache entry with timestamp for TTL-based invalidation
 */
interface CacheEntry {
  data: SponsorContactData;
  timestamp: number;
}

/**
 * SponsorContactService
 *
 * Fetches and caches sponsor contact data (locations, representatives, contact info)
 * from the Hub API. Uses client-side caching with a 10-minute TTL keyed by sponsor ID.
 *
 * Signal-based state management following the ChemicalService pattern.
 */
@Injectable({
  providedIn: 'root'
})
export class SponsorContactService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /** Cache TTL: 10 minutes */
  private readonly CACHE_TTL_MS = 10 * 60 * 1000;

  /** Client-side cache keyed by sponsor ID */
  private cache = new Map<string, CacheEntry>();

  // Signal-based state
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private contactDataSignal = signal<SponsorContactData | null>(null);

  /** Whether a fetch is currently in progress */
  readonly loading = computed(() => this.loadingSignal());

  /** Error message from the last failed fetch, or null */
  readonly error = computed(() => this.errorSignal());

  /** The most recently fetched sponsor contact data */
  readonly contactData = computed(() => this.contactDataSignal());

  /**
   * Fetch sponsor contact data by sponsor ID.
   * Returns cached data if a valid (non-expired) entry exists.
   * Otherwise fetches from GET /sponsors/:sponsorId/contacts.
   */
  async fetchContacts(sponsorId: string): Promise<void> {
    if (!sponsorId) {
      this.contactDataSignal.set(null);
      return;
    }

    // Check cache first
    const cached = this.cache.get(sponsorId);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL_MS) {
      this.contactDataSignal.set(cached.data);
      this.errorSignal.set(null);
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<SponsorContactData>>(
          `${this.apiUrl}/sponsors/${sponsorId}/contacts`
        )
      );

      if (response.success && response.data) {
        // Store in cache
        this.cache.set(sponsorId, {
          data: response.data,
          timestamp: Date.now()
        });
        this.contactDataSignal.set(response.data);
      } else {
        this.errorSignal.set(response.error || 'Failed to load sponsor contact data');
        this.contactDataSignal.set(null);
      }
    } catch (error: any) {
      console.error('Error fetching sponsor contacts:', error);
      this.errorSignal.set(error.message || 'Failed to load sponsor contact data');
      this.contactDataSignal.set(null);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Clear the entire cache. Useful for testing or manual invalidation.
   */
  clearCache(): void {
    this.cache.clear();
  }
}
